// Cari kartı (yeni / düzenle) durum ve kayıt mantığı. Yerleşim AddAccount.tsx ve accountCard/sections altında.
// Kayıt gövdesi eski AddAccount ile aynıdır; ek olarak authorizedPersonPhone ve isActive gönderilir.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ownAuthSession from '../../../../services/auth/ownAuthSession';
import firebaseDataService from '../../../../services/FirebaseDataService';
import { getCurrencySettings } from '../../../../services/migration/companyDataService';
import { useAuth } from '../../../../contexts/AuthContext';
import { ACCOUNTING_API, ECOMMERCE_API_URL, handleApiError } from '../../../../config/api';
import { accountingApi, ecommerceApi } from '../../../../config/apiClient';
import {
  lookupTaxpayer,
  loadActiveEInvoiceIntegration,
  isValidVknTckn,
  type ActiveEInvoiceIntegration,
  type TaxpayerLookupResult,
} from '../../e-invoice/services/taxpayerLookup';

export interface AccountGroup {
  id: string;
  name: string;
  parentId: string | null;
  level: number;
  createdAt: string;
  branchesId?: string | null;
  /** SQL'deki sayısal grup ID'si (string olarak) — id alanı Firebase ID taşıyabildiği için ayrı tutulur. */
  sqlId?: string | null;
}

/** Cari kişi türü: gerçek kişi (TCKN) / tüzel kişi (VKN). Boş = henüz belirlenmedi. */
export type PersonType = 'REAL' | 'LEGAL' | '';

/** Vergi numarasının uzunluğundan tür: 11 hane TCKN, 10 hane VKN. */
export const derivePersonType = (taxNumber?: string | null): PersonType => {
  const digits = (taxNumber || '').replace(/\D/g, '');
  if (digits.length === 11) return 'REAL';
  if (digits.length === 10) return 'LEGAL';
  return '';
};

export interface AccountData {
  personType: PersonType;
  companyName: string;
  groupId: string | null;
  authorizedPerson: string;
  authorizedPersonPhone: string;
  phone: string;
  mobilePhone: string;
  email: string;
  website: string;
  postalCode: string;
  taxOffice: string;
  taxNumber: string;
  tradeRegisterNumber: string;
  country: string;
  city: string;
  district: string;
  address: string;
  accountCode: string;
  notes: string;
  branchId: string | null;
  riskLimitAmount: string;
  riskLimitCurrency: string;
  paymentDueDays: string;
  riskLimitAction: string;
  preventNegativeBalance: boolean;
  customerRepresentative: string;
  isEcommerceUser: boolean;
  isB2BDealer: boolean;
  ecomUsername: string;
  ecomPassword: string;
  ecommerceDiscountPercent: string;
  ecommercePriceBasis: string;
  blockOrdersWhenOverdue: boolean;
}

export interface Branch {
  id: string;
  name: string;
}

export interface AddAccountProps {
  open: boolean;
  onClose: () => void;
  /** Düzenlenecek cari (varsa düzenleme modu) */
  editAccount?: any;
  /** E-faturadan gelen bilgiler ile ön doldurma */
  prefillData?: any;
  /** Manager panelinden açıldığında şube alanı bu şubeye kilitlenir */
  lockedBranchId?: string | null;
}

export interface GroupOption {
  group: AccountGroup;
  label: string;
  level: number;
  isParent: boolean;
  parentName?: string;
}

export const RISK_ACTION_ALLOW = 'işlem yaptır';
export const RISK_ACTION_BLOCK = 'işlem yaptırma';

const emptyAccount = (country: string): AccountData => ({
  personType: '',
  companyName: '',
  groupId: null,
  authorizedPerson: '',
  authorizedPersonPhone: '',
  phone: '',
  mobilePhone: '',
  email: '',
  website: '',
  postalCode: '',
  taxOffice: '',
  taxNumber: '',
  tradeRegisterNumber: '',
  country,
  city: '',
  district: '',
  address: '',
  accountCode: '',
  notes: '',
  branchId: null,
  riskLimitAmount: '0',
  riskLimitCurrency: 'TRY',
  paymentDueDays: '30',
  riskLimitAction: RISK_ACTION_ALLOW,
  preventNegativeBalance: false,
  customerRepresentative: '',
  isEcommerceUser: false,
  isB2BDealer: false,
  ecomUsername: '',
  ecomPassword: '',
  ecommerceDiscountPercent: '',
  ecommercePriceBasis: 'SalePrice',
  blockOrdersWhenOverdue: false,
});

/** Kayıt öncesi eksik/yanlış alan; section yerleşimde ilgili bölüme götürmek için kullanılır. */
export interface AccountProblem {
  section: 'identity' | 'taxinfo' | 'contact' | 'risk' | 'ecommerce' | 'other';
  message: string;
}

export const useAccountCardForm = ({ open, onClose, editAccount, prefillData, lockedBranchId = null }: AddAccountProps) => {
  const { t } = useTranslation('accounting');
  const { branches } = useAuth();
  const defaultCountry = t('current.modals.addAccount.defaults.country');

  const localizeGroupName = useCallback((name: string) => {
    const normalized = name.trim().toLocaleLowerCase('tr-TR');
    const keyMap: Record<string, string> = {
      'bireysel cari': 'individualCurrent',
      'personel cari': 'personnelCurrent',
      'personal cari': 'personnelCurrent',
      'kurumsal cari': 'corporateCurrent',
      'genel cari grubu': 'generalCurrentGroup',
    };
    const key = keyMap[normalized];
    return key ? t(`current.groupNames.${key}`, { defaultValue: name }) : name;
  }, [t]);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [isActive, setIsActive] = useState(true);
  // "Kaydet ve yeni ekle" sonrası bilgi
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  // Cariye bağlı mevcut e-ticaret üyesi (düzenleme modunda yüklenir)
  const [linkedMember, setLinkedMember] = useState<any | null>(null);
  // Cari kaydedildi ama e-ticaret üyesi adımı hata verdiyse, tekrar Kaydet'e
  // basıldığında yeni bir cari AÇILMAMASI için oluşturulan cari id'si saklanır.
  const createdAccountIdRef = useRef<number | null>(null);

  const isEditMode = !!editAccount;

  const [accountData, setAccountData] = useState<AccountData>(() => emptyAccount(defaultCountry));
  const [groups, setGroups] = useState<AccountGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<AccountGroup | null>(null);
  const [availableCurrencies, setAvailableCurrencies] = useState<{ code: string; active: boolean }[]>([
    { code: 'TRY', active: true },
  ]);

  // ===== Mükellef sorgusu =====
  const [lookup, setLookup] = useState<TaxpayerLookupResult | null>(null);
  const [lookupVkn, setLookupVkn] = useState<string>('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [noIntegrationOpen, setNoIntegrationOpen] = useState(false);
  // undefined = henüz yüklenmedi; null = entegrasyon yok
  const integrationRef = useRef<ActiveEInvoiceIntegration | null | undefined>(undefined);

  useEffect(() => {
    if (open) loadCompanyAndGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Düzenleme modunda mevcut veriyi yükle
  useEffect(() => {
    if (!(open && editAccount && groups.length > 0)) return;
    setAccountData({
      personType: (editAccount.personType as PersonType) || derivePersonType(editAccount.taxNumber),
      companyName: editAccount.companyName || '',
      groupId: editAccount.groupId || null,
      authorizedPerson: editAccount.authorizedPerson || '',
      authorizedPersonPhone: editAccount.authorizedPersonPhone || '',
      phone: editAccount.phone || '',
      mobilePhone: editAccount.mobilePhone || '',
      email: editAccount.email || '',
      website: editAccount.website || '',
      postalCode: editAccount.postalCode || '',
      taxOffice: editAccount.taxOffice || '',
      taxNumber: editAccount.taxNumber || '',
      tradeRegisterNumber: editAccount.tradeRegisterNumber || '',
      country: editAccount.country || defaultCountry,
      city: editAccount.city || '',
      district: editAccount.district || '',
      address: editAccount.address || '',
      accountCode: editAccount.accountCode || '',
      notes: editAccount.notes || '',
      branchId: editAccount.branchId || null,
      // Sunucuda boş = sınırsız; düzenlemede boş değer korunur (eskiden '0'a çevrilip veresiye kapanıyordu).
      riskLimitAmount: editAccount.riskLimitAmount === null || editAccount.riskLimitAmount === undefined
        ? ''
        : String(editAccount.riskLimitAmount),
      riskLimitCurrency: editAccount.riskLimitCurrency || 'TRY',
      paymentDueDays: editAccount.paymentDueDays?.toString() || '30',
      riskLimitAction: editAccount.riskLimitAction || RISK_ACTION_ALLOW,
      preventNegativeBalance: editAccount.preventNegativeBalance || false,
      customerRepresentative: editAccount.customerRepresentative || '',
      isEcommerceUser: editAccount.isEcommerceUser || false,
      isB2BDealer: editAccount.isB2BDealer || false,
      ecomUsername: '',
      ecomPassword: '',
      ecommerceDiscountPercent: editAccount.ecommerceDiscountPercent?.toString() || '',
      ecommercePriceBasis: editAccount.ecommercePriceBasis || 'SalePrice',
      blockOrdersWhenOverdue: editAccount.blockOrdersWhenOverdue || false,
    });
    setIsActive(editAccount.isActive !== undefined ? !!editAccount.isActive : true);

    // Carideki grup referansı Firebase ID veya SQL ID olabilir
    const grp = groups.find(g => g.id === editAccount.groupId || (g.sqlId && g.sqlId === editAccount.groupId));
    setSelectedGroup(grp || null);
    setSelectedBranch(branches.find(b => b.id === editAccount.branchId) || null);

    // Cariye bağlı e-ticaret üyesi varsa kullanıcı adını göster
    const numericId = parseInt(editAccount.id, 10);
    if (!isNaN(numericId)) {
      ecommerceApi
        .get(`${ECOMMERCE_API_URL}/api/ecommerce/members/auth/by-account/${numericId}`)
        .then(res => {
          const m = res.data?.data;
          if (m) {
            setLinkedMember(m);
            setAccountData(prev => ({
              ...prev,
              isEcommerceUser: true,
              isB2BDealer: prev.isB2BDealer || !!m.isB2B,
              ecomUsername: m.username || '',
            }));
          } else {
            setLinkedMember(null);
          }
        })
        .catch(err => console.warn('[AddAccount] Bağlı e-ticaret üyesi sorgulanamadı:', err));
    }
  }, [open, editAccount, groups, branches, defaultCountry]);

  // E-faturadan gelen bilgiler ile ön doldurma
  useEffect(() => {
    if (!(open && prefillData && !editAccount)) return;
    setAccountData({
      ...emptyAccount(prefillData.country || defaultCountry),
      // Vergi no uzunluğu kişi türünü belirler; eşleştirme akışı tür seçimi beklemeden devam eder.
      personType: derivePersonType(prefillData.taxNumber),
      companyName: prefillData.companyName || '',
      authorizedPerson: prefillData.authorizedPerson || '',
      phone: prefillData.phone || '',
      email: prefillData.email || '',
      website: prefillData.website || '',
      taxOffice: prefillData.taxOffice || '',
      taxNumber: prefillData.taxNumber || '',
      city: prefillData.city || '',
      district: prefillData.district || '',
      address: prefillData.address || '',
      notes: t('current.modals.addAccount.defaults.prefillNote'),
    });
  }, [open, prefillData, editAccount, t, defaultCountry]);

  // Manager: şube alanı kilitli — manager'ın şubesi otomatik seçilir
  useEffect(() => {
    if (open && lockedBranchId) {
      const lockedBr = branches.find(b => b.id === lockedBranchId);
      if (lockedBr) {
        setSelectedBranch(lockedBr);
        setAccountData(prev => ({ ...prev, branchId: lockedBr.id }));
      }
    }
  }, [open, lockedBranchId, branches]);

  const loadAccountGroups = async () => {
    try {
      const response = await accountingApi.get(ACCOUNTING_API.ACCOUNT_GROUP_BY_COMPANY);
      const result = response.data;
      const sqlGroups = result?.data || result || [];
      const list: AccountGroup[] = (Array.isArray(sqlGroups) ? sqlGroups : []).map((data: any) => ({
        id: data.firebaseId || data.id.toString(),
        sqlId: data.id != null ? data.id.toString() : null,
        name: data.name || '',
        parentId: data.parentFirebaseId || null,
        level: data.level || 1,
        createdAt: data.createdAt || '',
        branchesId: data.branchId || null,
      }));
      list.sort((a, b) => (a.level !== b.level ? a.level - b.level : a.name.localeCompare(b.name)));
      setGroups(list);
    } catch (err) {
      console.error('SQL cari grupları yükleme hatası:', err);
      setError(handleApiError(err));
    } finally {
      setInitialLoading(false);
    }
  };

  const loadCurrencies = async (cid: string) => {
    try {
      const currencyData = await getCurrencySettings(cid);
      const currencies: { code: string; active: boolean }[] = [{ code: 'TRY', active: true }];
      if (currencyData) {
        Object.entries(currencyData).forEach(([code, active]: [string, any]) => {
          if (code !== 'TRY' && active === true) currencies.push({ code, active: true });
        });
      }
      setAvailableCurrencies(currencies);
      setAccountData(prev => (currencies.some(c => c.code === prev.riskLimitCurrency) ? prev : { ...prev, riskLimitCurrency: 'TRY' }));
    } catch (err) {
      console.error('Para birimi yükleme hatası:', err);
      setAvailableCurrencies([{ code: 'TRY', active: true }]);
    }
  };

  const loadCompanyAndGroups = async () => {
    try {
      setInitialLoading(true);
      const uid = ownAuthSession.getCurrentUid();
      if (!uid) {
        setInitialLoading(false);
        setError(t('current.modals.addAccount.errors.userNotLoggedIn'));
        return;
      }
      const userData = await firebaseDataService.getUserData(uid);
      if (!userData) {
        setInitialLoading(false);
        setError(t('current.modals.addAccount.errors.userNotFound'));
        return;
      }
      const cid = userData.companyId;
      setCompanyId(cid || null);
      if (!cid) {
        setInitialLoading(false);
        setError(t('current.modals.addAccount.errors.companyNotFound'));
        return;
      }
      loadAccountGroups();
      loadCurrencies(cid);
    } catch (err) {
      console.error('Şirket veya grup verileri yüklenirken hata:', err);
      setInitialLoading(false);
      setError(t('current.modals.addAccount.errors.dataLoad'));
    }
  };

  const getCurrencySymbol = (code: string): string => {
    switch (code) {
      case 'TRY': return '₺';
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'JPY': return '¥';
      case 'CHF': return 'Fr.';
      case 'AUD': return 'A$';
      case 'CAD': return 'C$';
      case 'CNY': return '¥';
      default: return code;
    }
  };

  const resetForm = () => {
    // Manager'da şube kilitli kalır
    const lockedBr = lockedBranchId ? branches.find(b => b.id === lockedBranchId) || null : null;
    setAccountData({ ...emptyAccount(defaultCountry), branchId: lockedBr?.id ?? null });
    setSelectedGroup(null);
    setSelectedBranch(lockedBr);
    setLinkedMember(null);
    setIsActive(true);
    setLookup(null);
    setLookupVkn('');
    setNoIntegrationOpen(false);
    createdAccountIdRef.current = null;
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    setSuccessMsg(null);
    onClose();
  };

  const handleChange = <K extends keyof AccountData>(field: K, value: AccountData[K]) => {
    setAccountData(prev => ({ ...prev, [field]: value }));
  };

  const handleGroupChange = (value: AccountGroup | null) => {
    setSelectedGroup(value);
    setAccountData(prev => ({ ...prev, groupId: value ? value.id : null }));
  };

  const handleBranchChange = (value: Branch | null) => {
    setSelectedBranch(value);
    setAccountData(prev => ({ ...prev, branchId: value ? value.id : null }));
  };

  const isRealPerson = accountData.personType === 'REAL';
  const isLegalPerson = accountData.personType === 'LEGAL';

  // Gerçek kişide yetkili kişi alanları kullanılmaz; gizlenen alanlarda eski değer kalmasın.
  const handlePersonTypeChange = (value: Exclude<PersonType, ''>) => {
    setAccountData(prev => ({
      ...prev,
      personType: value,
      authorizedPerson: value === 'REAL' ? '' : prev.authorizedPerson,
      authorizedPersonPhone: value === 'REAL' ? '' : prev.authorizedPersonPhone,
    }));
    setError(null);
  };

  // Yalnız rakam, en çok 11 hane. 10 hane VKN -> tüzel, 11 hane TCKN -> gerçek kişi.
  const handleTaxNumberChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 11);
    const derived = derivePersonType(digits);
    setAccountData(prev => ({
      ...prev,
      taxNumber: digits,
      personType: derived || prev.personType,
      authorizedPerson: derived === 'REAL' ? '' : prev.authorizedPerson,
      authorizedPersonPhone: derived === 'REAL' ? '' : prev.authorizedPersonPhone,
    }));
  };

  // Vergi no değişince eski sorgu sonucu geçersizdir.
  const lookupMatches = lookup !== null && lookupVkn === accountData.taxNumber;

  const runLookup = async () => {
    const vkn = accountData.taxNumber.trim();
    if (!isValidVknTckn(vkn)) {
      setLookup({ status: 'invalid' });
      setLookupVkn(vkn);
      return;
    }
    setLookupLoading(true);
    try {
      if (integrationRef.current === undefined || integrationRef.current === null) {
        // Entegrasyon yoksa her tıklamada yeniden bakılır: kullanıcı başka sekmede eklemiş olabilir.
        try {
          integrationRef.current = await loadActiveEInvoiceIntegration();
        } catch (err) {
          setLookup({ status: 'error', message: handleApiError(err) });
          setLookupVkn(vkn);
          return;
        }
      }
      if (!integrationRef.current) {
        setLookup({ status: 'no-integration' });
        setLookupVkn(vkn);
        setNoIntegrationOpen(true);
        return;
      }
      const res = await lookupTaxpayer(vkn, integrationRef.current);
      setLookup(res);
      setLookupVkn(vkn);
      // Unvan boşsa GİB unvanı doğrudan yazılır; doluysa öneri olarak gösterilir.
      if (res.status === 'found' && res.title && !accountData.companyName.trim()) {
        setAccountData(prev => ({ ...prev, companyName: res.title }));
      }
    } finally {
      setLookupLoading(false);
    }
  };

  const applyLookupTitle = () => {
    if (lookup?.status === 'found' && lookup.title) handleChange('companyName', lookup.title);
  };

  const titleSuggestion = lookupMatches && lookup?.status === 'found' && lookup.title
    && lookup.title.trim() !== accountData.companyName.trim()
    ? lookup.title
    : null;

  // Grup adını tam yoluyla göster
  const getGroupFullPath = (group: AccountGroup | null): string => {
    if (!group) return '';
    const parts: string[] = [localizeGroupName(group.name)];
    const byId = new Map(groups.map(g => [g.id, g]));
    let parentGroup = group.parentId ? byId.get(group.parentId) : undefined;
    // Bozuk veride döngüye girmemek için derinlik sınırı
    for (let depth = 0; parentGroup && depth < 10; depth++) {
      parts.unshift(localizeGroupName(parentGroup.name));
      parentGroup = parentGroup.parentId ? byId.get(parentGroup.parentId) : undefined;
    }
    return parts.join(' > ');
  };

  const handleOpenGroupModal = () => setIsGroupModalOpen(true);
  const handleCloseGroupModal = () => {
    setIsGroupModalOpen(false);
    if (companyId) loadAccountGroups();
  };

  // Ana grup + iki seviye alt grup, hiyerarşik sırada
  const getFilteredGroupOptions = (): GroupOption[] => {
    const options: GroupOption[] = [];
    groups.filter(g => g.level === 1).forEach(root => {
      options.push({ group: root, label: localizeGroupName(root.name), level: root.level, isParent: true });
      groups.filter(g => g.parentId === root.id).forEach(sub => {
        options.push({
          group: sub, label: localizeGroupName(sub.name), level: sub.level, isParent: false,
          parentName: localizeGroupName(root.name),
        });
        groups.filter(g => g.parentId === sub.id).forEach(subSub => {
          options.push({
            group: subSub, label: localizeGroupName(subSub.name), level: subSub.level, isParent: false,
            parentName: `${localizeGroupName(root.name)} > ${localizeGroupName(sub.name)}`,
          });
        });
      });
    });
    return options;
  };

  // ===== Doğrulama =====
  const taxDigits = accountData.taxNumber.replace(/\D/g, '');
  const problems: AccountProblem[] = [];
  if (!accountData.personType) {
    problems.push({ section: 'identity', message: t('current.modals.addAccount.validation.personTypeRequired') });
  }
  if (!accountData.companyName.trim()) {
    problems.push({
      section: 'identity',
      message: isRealPerson
        ? t('current.modals.addAccount.validation.fullNameRequired')
        : t('current.modals.addAccount.validation.companyTitleRequired'),
    });
  }
  if (!accountData.groupId) {
    problems.push({ section: 'identity', message: t('current.modals.addAccount.validation.groupRequired') });
  }
  // Vergi kimlik no tüzel kişide zorunlu (10 hane); gerçek kişide opsiyonel ama girildiyse 11 hane.
  if (isLegalPerson && !taxDigits) {
    problems.push({ section: 'identity', message: t('current.modals.addAccount.validation.taxNumberRequired') });
  } else if (isLegalPerson && taxDigits.length !== 10) {
    problems.push({ section: 'identity', message: t('current.modals.addAccount.validation.taxNumberLength') });
  } else if (isRealPerson && taxDigits && taxDigits.length !== 11) {
    problems.push({ section: 'identity', message: t('current.modals.addAccount.validation.identityNumberLength') });
  }
  if (accountData.isEcommerceUser) {
    if (!accountData.ecomUsername.trim()) {
      problems.push({ section: 'ecommerce', message: t('current.modals.addAccount.b2b.validation.usernameRequired', { defaultValue: 'E-ticaret kullanıcısı için kullanıcı adı zorunludur.' }) });
    }
    // E-posta cari kaydından önce doğrulanır: create-from-account e-posta ister; eksikse
    // cari oluşup üye adımı patlıyordu.
    if (!accountData.email.trim()) {
      problems.push({ section: 'contact', message: t('current.modals.addAccount.b2b.validation.emailRequired', { defaultValue: 'E-ticaret kullanıcısı için e-posta zorunludur.' }) });
    }
    if (!linkedMember && !accountData.ecomPassword) {
      problems.push({ section: 'ecommerce', message: t('current.modals.addAccount.b2b.validation.passwordRequired', { defaultValue: 'Yeni e-ticaret kullanıcısı için şifre zorunludur.' }) });
    }
    if (accountData.ecomPassword && accountData.ecomPassword.length < 6) {
      problems.push({ section: 'ecommerce', message: t('current.modals.addAccount.b2b.validation.passwordMinLength', { defaultValue: 'Şifre en az 6 karakter olmalıdır.' }) });
    }
  }
  if (accountData.ecommerceDiscountPercent) {
    const pct = parseFloat(accountData.ecommerceDiscountPercent);
    if (isNaN(pct) || pct < 0 || pct > 100) {
      problems.push({ section: 'ecommerce', message: t('current.modals.addAccount.b2b.validation.discountRange', { defaultValue: 'İskonto yüzdesi 0-100 arasında olmalıdır.' }) });
    }
  }

  const buildPayload = (cid: string) => ({
    companyId: cid,
    companyName: accountData.companyName.trim(),
    groupFirebaseId: selectedGroup?.id ?? accountData.groupId,
    // Sayısal FK da gönderilir: liste/rapor sorguları GroupId üzerinden filtreliyor.
    groupId: selectedGroup?.sqlId ? parseInt(selectedGroup.sqlId, 10) : null,
    personType: accountData.personType || null,
    authorizedPerson: isRealPerson ? '' : accountData.authorizedPerson.trim(),
    authorizedPersonPhone: isRealPerson ? '' : accountData.authorizedPersonPhone.trim(),
    phone: accountData.phone.trim(),
    mobilePhone: accountData.mobilePhone.trim(),
    email: accountData.email.trim(),
    website: accountData.website.trim(),
    postalCode: accountData.postalCode.trim(),
    taxOffice: accountData.taxOffice.trim(),
    taxNumber: accountData.taxNumber.trim(),
    tradeRegisterNumber: accountData.tradeRegisterNumber.trim(),
    country: accountData.country.trim(),
    city: accountData.city.trim(),
    district: accountData.district.trim(),
    address: accountData.address.trim(),
    accountCode: accountData.accountCode.trim() || '',
    notes: accountData.notes.trim(),
    branchId: selectedBranch?.id || null,
    riskLimitAmount: accountData.riskLimitAmount !== '' ? parseFloat(accountData.riskLimitAmount) : null,
    riskLimitCurrency: accountData.riskLimitCurrency || 'TRY',
    paymentDueDays: accountData.paymentDueDays ? parseInt(accountData.paymentDueDays, 10) : null,
    riskLimitAction: accountData.riskLimitAction || null,
    preventNegativeBalance: accountData.preventNegativeBalance || false,
    customerRepresentative: accountData.customerRepresentative.trim() || null,
    ecommerceDiscountPercent: accountData.ecommerceDiscountPercent ? parseFloat(accountData.ecommerceDiscountPercent) : null,
    ecommercePriceBasis: accountData.ecommercePriceBasis || 'SalePrice',
    blockOrdersWhenOverdue: accountData.blockOrdersWhenOverdue || false,
    isActive,
  });

  // Yeni ekleme veya güncelleme. keepOpenForNext: kayıttan sonra form boşaltılır, pencere açık kalır.
  const handleSubmit = async (keepOpenForNext = false) => {
    if (problems.length > 0) {
      setError(problems.map(p => p.message).join(' · '));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      if (!companyId) throw new Error(t('current.modals.addAccount.errors.companyNotFound'));
      const accountPayload: any = buildPayload(companyId);

      let apiUrl: string;
      if (isEditMode) {
        const numericId = parseInt(editAccount.id, 10);
        if (isNaN(numericId)) throw new Error(t('current.modals.addAccount.errors.invalidAccountId'));
        accountPayload.id = numericId;
        accountPayload.updatedAt = new Date().toISOString();
        apiUrl = ACCOUNTING_API.ACCOUNT_UPDATE(numericId);
      } else if (createdAccountIdRef.current) {
        // Cari önceki denemede oluşturuldu (üye adımı hata vermişti): duplicate açılmaz, güncellenir.
        accountPayload.id = createdAccountIdRef.current;
        accountPayload.updatedAt = new Date().toISOString();
        apiUrl = ACCOUNTING_API.ACCOUNT_UPDATE(createdAccountIdRef.current);
      } else {
        apiUrl = ACCOUNTING_API.ACCOUNTS;
      }

      const response = await accountingApi.post(apiUrl, accountPayload);
      const result = response.data;

      if (!isEditMode && !createdAccountIdRef.current) {
        const newId = result?.data?.id ?? result?.id;
        if (newId) createdAccountIdRef.current = Number(newId);
      }

      // E-ticaret kullanıcısı tiki işaretliyse üye oluştur/güncelle
      if (accountData.isEcommerceUser) {
        const savedAccountId = isEditMode
          ? parseInt(editAccount.id, 10)
          : (createdAccountIdRef.current ?? (result?.data?.id ?? result?.id));
        if (savedAccountId) {
          try {
            const memberPayload: any = {
              accountId: savedAccountId,
              username: accountData.ecomUsername.trim(),
              isB2B: accountData.isB2BDealer,
              email: accountData.email.trim() || undefined,
            };
            if (accountData.ecomPassword) memberPayload.password = accountData.ecomPassword;
            await ecommerceApi.post(`${ECOMMERCE_API_URL}/api/ecommerce/members/auth/create-from-account`, memberPayload);
          } catch (memberError: any) {
            console.error('❌ [B2B] E-ticaret üyesi oluşturulamadı:', memberError);
            setError(memberError?.response?.data?.message
              || t('current.modals.addAccount.b2b.errors.memberCreateFailed', { defaultValue: 'Cari kaydedildi ancak e-ticaret kullanıcısı oluşturulamadı.' }));
            setLoading(false);
            return; // Modal açık kalır, kullanıcı düzeltip tekrar kaydedebilir
          }
        }
      }

      if (keepOpenForNext && !isEditMode) {
        const savedName = accountData.companyName.trim();
        resetForm();
        setSuccessMsg(t('current.modals.addAccount.layout.savedNext', '"{{name}}" kaydedildi. Yeni cariyi girebilirsiniz.', { name: savedName }));
        return;
      }
      handleClose();
    } catch (err: any) {
      console.error(`❌ Cari hesap ${isEditMode ? 'güncellenirken' : 'kaydedilirken'} hata:`, err);
      const errorData = err?.response?.data;
      if (errorData) {
        let userMessage = errorData.message || (isEditMode
          ? t('current.modals.addAccount.errors.updateFailed')
          : t('current.modals.addAccount.errors.saveFailed'));
        if (typeof userMessage === 'string' && userMessage.includes('cari kodu zaten kullanılıyor')) {
          userMessage = t('current.modals.addAccount.errors.accountCodeDuplicate');
        }
        setError(userMessage);
      } else {
        setError(err instanceof Error ? err.message : t('current.modals.addAccount.errors.unknown'));
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    open, isEditMode, editAccount, lockedBranchId, isPrefill: !!prefillData && !editAccount,
    successMsg, setSuccessMsg,
    loading, initialLoading, error, setError,
    accountData, handleChange, isActive, setIsActive,
    isRealPerson, isLegalPerson, handlePersonTypeChange, handleTaxNumberChange,
    groups, selectedGroup, handleGroupChange, getFilteredGroupOptions, getGroupFullPath, localizeGroupName,
    isGroupModalOpen, handleOpenGroupModal, handleCloseGroupModal,
    branches: branches as Branch[], selectedBranch, handleBranchChange,
    availableCurrencies, getCurrencySymbol,
    linkedMember,
    lookup: lookupMatches ? lookup : null, lookupLoading, runLookup, applyLookupTitle, titleSuggestion,
    noIntegrationOpen, setNoIntegrationOpen,
    problems,
    handleSubmit, handleClose,
  };
};

export type AccountCardForm = ReturnType<typeof useAccountCardForm>;

